import React, {useEffect, useState, useContext, useMemo, useCallback, useRef} from "react";
import PropTypes from "prop-types";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClose} from "@fortawesome/free-solid-svg-icons";
import {useNavigate, useParams} from "react-router-dom";

// Contexts
import {AuthContext} from "../../contexts/auth.js";
import {NavContext} from "../../contexts/nav.js";

// Hooks
import useApi from "../../hooks/useApi.js";
import usePrevious from "../../hooks/usePrevious.js";

// Utils
import {exists, toTitleCase} from "../../utils/helpers.js";
import {
  COMPONENT,
  FIELD,
  GROUP,
  getElementPosition,
  getPositionOptions,
  labelAlreadyExists
} from "../../utils/builder.js";
import {DEFAULT_MARKER} from "../../utils/google/maps.js";

// Components
import Modal from "../../components/Modal.js";
import ModalEditConflict from "./ModalEditConflict.js";
import Location from "../../components/Location.js";
import SearchSelect from "../../components/SearchSelect.js";
import Help from "../../components/Help.js";
import ElementHelp from "../general/ElementHelp.js";
import ElementFieldHelp from "../general/ElementFieldHelp.js";
import FieldBuilder from "../../components/FieldBuilder.js";
import {
  InputCheck,
  InputFileGroup,
  InputText,
  InputTextArea,
  InputSelect,
  InputTextGroup
} from "../../components/form/FormInputs.js";

// Style
import {pad} from "../../style/components/variables.js";
import {voice} from "../../style/components/typography.js";
import {
  HeadingCenter,
  Button,
  FormGroup,
  FormField,
  Form,
  Text,
  SearchWrapper,
  Pill,
  Warning,
  Inline
} from "../../style/components/general.js";

const ModalEditElement = ({
  visible,
  setVisible,
  targetElement,
  setTargetElement,
  persistEdit,
  builder,
  markerIconMap,
  checksheets,
  units,
  saveWarningAction
}) => {
  const markers = Object.keys(markerIconMap).map(id => ({id, ...markerIconMap[id]}));

  const {slug} = useParams();

  const stored = useRef();
  const navigate = useNavigate();

  const {currentUser} = useContext(AuthContext);
  const {intermediateAction} = useContext(NavContext);

  const [triggerAppend, setTriggerAppend] = useState(null);
  const [results, setResults] = useState(markers);
  const [tempValues, setTempValues] = useState();
  const [restrictedError, setRestrictedError] = useState(null);
  const [acceptableError, setAcceptableError] = useState(null);
  const [rangeError, setRangeError] = useState(null);
  const [showModalConfirm, setShowModalConfirm] = useState(false);
  const [showModalConflict, setShowModalConflict] = useState(false);

  const {api: uploadFile} = useApi("files");

  const prevSaveWarningAction = usePrevious(saveWarningAction);
  const prevShowConflictModal = usePrevious(showModalConflict);

  const checksheetsUsingTarget = useMemo(
    () =>
      checksheets?.filter(
        ({builder: checksheetBuilder}) => targetElement.name in checksheetBuilder.byId
      ),
    [checksheets, targetElement]
  );

  const checkhseetsUsingTargetNoSiblings = useMemo(
    () =>
      checksheetsUsingTarget?.filter(({builder: checksheetBuilder}) => {
        if (!(targetElement.name in checksheetBuilder.byId)) return false;

        const {parentName} = checksheetBuilder.byId[targetElement.name];
        const parentElement = parentName ? checksheetBuilder.byId[parentName] : undefined;
        const siblingList = parentElement?.children || checksheetBuilder.allIds;

        return !siblingList?.filter(
          sibling =>
            sibling !== targetElement.name &&
            checksheetBuilder.byId[sibling] &&
            !checksheetBuilder.byId[sibling].deleted
        )?.length;
      }),
    [checksheetsUsingTarget, targetElement]
  );

  const hasChildFields = useMemo(() => {
    const {children} = targetElement;
    const fieldHelp = children?.filter(child => builder.byId[child]?.element === FIELD);
    return fieldHelp?.length > 0;
  }, [builder, targetElement]);

  const defaultValues = useMemo(() => {
    const {help: allHelp, children, element} = targetElement;

    let generalHelp = null;
    let fieldHelp = null;

    if (element === FIELD && targetElement.parentName && targetElement.parentName in builder.byId) {
      const parentElement = builder.byId[targetElement.parentName];
      const help = parentElement?.help?.filter(h => h.id === targetElement.name);
      if (help?.length > 0) generalHelp = help[0].value;
    } else {
      const fieldHelpItems = allHelp?.filter(({id}) => !!id);
      generalHelp = allHelp?.filter(({id}) => !id);

      fieldHelp = children
        ?.filter(child => builder.byId[child]?.element === FIELD)
        .map(child => {
          const found = fieldHelpItems?.filter(({id}) => id === child);
          if (found?.length > 0) return {...found[0], enabled: true};
          return {key: builder.byId[child].label, value: null, id: child, enabled: false};
        });
    }

    const fallbackHelp = element === FIELD ? "" : [{key: null, value: null}];

    if (generalHelp?.length === 0) generalHelp = fallbackHelp;

    if (element === FIELD)
      return {
        ...targetElement,
        type: targetElement.type || "number",
        label: targetElement.label || "",
        grid: targetElement.grid,
        hasGlobalPrompt: "globalPrompt" in targetElement && targetElement.element === GROUP,
        globalPrompt: targetElement.element === GROUP ? targetElement?.globalPrompt : null,
        hasInternalIds: "internalIds" in targetElement && targetElement.element === GROUP,
        internalIds: targetElement.element === GROUP ? targetElement?.internalIds : null,
        hasAddress: "address" in targetElement && targetElement.element === GROUP,
        address: targetElement.element === GROUP ? targetElement?.address : null,
        markerId: targetElement.element === GROUP ? targetElement?.markerId : null,
        hasLimits: targetElement.hasLimits || false,
        limits: targetElement.limits || [{}],
        hasHelp: "help" in targetElement,
        help: generalHelp || fallbackHelp
      };

    return {
      label: targetElement.label || "",
      grid: targetElement.grid,
      hasGlobalPrompt: "globalPrompt" in targetElement && targetElement.element === GROUP,
      globalPrompt: targetElement.element === GROUP ? targetElement?.globalPrompt : null,
      hasInternalIds: "internalIds" in targetElement && targetElement.element === GROUP,
      internalIds: targetElement.element === GROUP ? targetElement?.internalIds : null,
      hasAddress: "address" in targetElement && targetElement.element === GROUP,
      address: targetElement.element === GROUP ? targetElement?.address : null,
      markerId: targetElement.element === GROUP ? targetElement?.markerId : null,
      hasHelp: "help" in targetElement,
      help: generalHelp || fallbackHelp,
      fieldHelp,
      allowDisable:
        targetElement.element === COMPONENT ? targetElement.allowDisable || false : null,
      enabledLabel:
        targetElement.element === COMPONENT ? targetElement.enabledLabel || "In Service" : null,
      disabledLabel:
        targetElement.element === COMPONENT ? targetElement.disabledLabel || "Out of Service" : null,
      linkedGroup: targetElement.element === COMPONENT ? targetElement.parentName || "" : undefined,
      position:
        targetElement.element === GROUP || targetElement.element === COMPONENT
          ? getElementPosition(builder, targetElement.name)
          : undefined
    };
  }, [builder, targetElement]);

  const schema = yup.object().shape({
    label: yup
      .string()
      .required("Please provide an Element Name")
      .test({
        test: val => !val.match(/.*[.,'"].*/),
        message: "Element name may not contain periods, commas or quotation marks."
      })
      .test({
        name: "label-uniqueness-test",
        test: (value, ctx) => {
          if (!builder) return true;

          const newParent = ctx.parent.linkedGroup || targetElement.parentName;

          if (labelAlreadyExists(value, targetElement.name, newParent, builder))
            return ctx.createError({
              message: newParent
                ? `Field name already exists in ${builder.byId[newParent].label}`
                : "Field name already exists on top level",
              path: ctx.path
            });

          return true;
        }
      }),
    hasGlobalPrompt: yup.boolean(),
    globalPrompt: yup.string().nullable(),
    base: yup.bool(),
    displayTable: yup.bool(),
    hasInternalIds: yup.bool(),
    internalIds: yup
      .mixed()
      .nullable()
      .when("hasInternalIds", {
        is: val => !!val && targetElement.element === GROUP,
        then: () =>
          yup
            .array()
            .of(
              yup.object().shape({
                key: yup.string().nullable().required("Please provide a key."),
                value: yup.string().nullable().required("Please provide a value.")
              })
            )
            .test({
              test: val => Array.isArray(val) && val.length > 0,
              message: "Please provide internal id."
            })
      }),
    hasHelp: yup.bool(),
    help: yup
      .mixed()
      .nullable()
      .when("hasHelp", {
        is: val => !!val,
        then: () =>
          yup
            .array()
            .of(
              yup.object().shape({
                key: yup.string().nullable().required("Please provide a key."),
                value: yup.string().nullable().required("Please provide a value.")
              })
            )
            .test({
              test: val => Array.isArray(val) && val.length > 0,
              message: "Please provide help item."
            })
      }),
    hasAddress: yup.bool(),
    address: yup
      .mixed()
      .nullable()
      .when("hasAddress", {
        is: val => !!val && targetElement.element === GROUP,
        then: () =>
          yup.lazy(value => {
            const {lat, lon, line1} = value || {};
            if ((!lat || lat === "") && (!lon || lon === "") && line1 !== "" && line1 !== undefined)
              return yup.object().shape({
                line1: yup.string().required("Line 1 is required."),
                line2: yup.string().nullable(),
                city: yup.string().required("City is required."),
                state: yup.string().required("State is required."),
                zipCode: yup.string().required("Zip Code is required."),
                lat: yup.number().nullable(),
                lon: yup.number().nullable()
              });

            if (
              (!line1 || line1 === "") &&
              lat !== "" &&
              lat !== undefined &&
              lon !== "" &&
              lon !== undefined
            )
              return yup.object().shape({
                line1: yup.string().nullable(),
                line2: yup.string().nullable(),
                city: yup.string().nullable(),
                state: yup.string().nullable(),
                zipCode: yup.string().nullable(),
                lat: yup
                  .number()
                  .typeError("Provide valid latitude.")
                  .required("Latitude is required."),
                lon: yup
                  .number()
                  .typeError("Provide valid longitude.")
                  .required("Longitude is required.")
              });

            return yup.object().shape({
              line1: yup.string().required("Line 1 is required."),
              line2: yup.string().nullable(),
              city: yup.string().required("City is required."),
              state: yup.string().required("State is required."),
              zipCode: yup.string().required("Zip Code is required."),
              lat: yup
                .number()
                .typeError("Provide valid latitude.")
                .required("Latitude is required."),
              lon: yup
                .number()
                .typeError("Provide valid longitude.")
                .required("Longitude is required.")
            });
          })
      }),
    markerId: yup.string().nullable(),
    linkedGroup: yup.string().nullable(),
    position: yup.string().nullable(),
    // allowDisable/enabledLabel/disabledLabel configure this component's Component Service State (in/out of service)
    allowDisable: yup.bool().nullable(),
    enabledLabel: yup
      .string()
      .nullable()
      .when("allowDisable", {
        is: val => !!val,
        then: () => yup.string().nullable().required("Please provide a label for enabled state.")
      }),
    disabledLabel: yup
      .string()
      .nullable()
      .when("allowDisable", {
        is: val => !!val,
        then: () => yup.string().nullable().required("Please provide a label for disabled state.")
      })
  });

  const form = useForm({
    defaultValues,
    resolver: yupResolver(schema)
  });
  const {watch, setValue, handleSubmit, reset} = form;
  const watchElement = watch();
  const watchLinkedGroup = watch("linkedGroup");
  const watchHasARange = watch("hasARange");
  const watchAMin = watch("aMin");
  const watchAMax = watch("aMax");
  const watchHasRange = watch("hasRange");
  const watchMin = watch("min");
  const watchMax = watch("max");
  const watchAllowDisable = watch("allowDisable");

  useEffect(() => {
    if (
      tempValues &&
      ((prevSaveWarningAction && !saveWarningAction) ||
        (prevShowConflictModal && !showModalConflict))
    ) {
      reset(tempValues);
      setTempValues(undefined);
    }
  }, [
    prevSaveWarningAction,
    saveWarningAction,
    prevShowConflictModal,
    showModalConflict,
    reset,
    tempValues
  ]);

  useEffect(() => {
    setRestrictedError(null);
  }, [watchHasRange, watchMin, watchMax]);

  useEffect(() => {
    setAcceptableError(null);
  }, [watchHasARange, watchAMin, watchAMax]);

  const parentChanged = useMemo(() => {
    const {linkedGroup} = watchElement;
    let elementParent;
    if (linkedGroup && linkedGroup !== "") elementParent = linkedGroup;
    else elementParent = targetElement.parentName || "";
    return elementParent !== targetElement.parentName;
  }, [watchElement, targetElement]);

  // Matches handleSave: an empty linkedGroup keeps the current parent
  const effectiveParentName =
    (targetElement.element === COMPONENT && watchLinkedGroup) || targetElement.parentName || "";

  const positionOptions = useMemo(() => {
    if (targetElement.element !== GROUP && targetElement.element !== COMPONENT) return [];
    return getPositionOptions(builder, targetElement.name, effectiveParentName);
  }, [builder, targetElement, effectiveParentName]);

  // Reparenting resets Position to the slot updateElement will use (end of the new parent's list)
  useEffect(() => {
    if (targetElement.element !== COMPONENT) return;
    setValue("position", getElementPosition(builder, targetElement.name, effectiveParentName));
  }, [builder, effectiveParentName, setValue, targetElement]);

  const addFile = file => {
    const formData = new FormData();
    formData.append("file", file);
    uploadFile
      .callPost(formData, {
        params: {
          userId: currentUser.publicId
        }
      })
      .then(({status, data: body}) => {
        if (status === 201) {
          let wasSet = false;
          if (watchElement.help?.length > 0)
            for (let i = 0; i < watchElement.help.length; i++) {
              const {key, value} = watchElement.help[i] || {};
              if (!key && !value) {
                setValue(`help.${i}`, {
                  key: body.data.label.split(".")[0],
                  value: body.data.label,
                  fileId: body.data.id,
                  export: body.data.export || false
                });
                wasSet = true;
                break;
              }
            }

          if (!wasSet)
            setTriggerAppend({
              key: body.data.label.split(".")[0],
              value: body.data.label,
              fileId: body.data.id,
              export: body.data.export || false
            });
        }
      });
  };

  useEffect(() => {
    if (!visible) {
      setTargetElement(null);
      reset({...defaultValues});
    }
  }, [defaultValues, targetElement, visible, reset, setTargetElement]);

  const search = query => {
    if (query) {
      const filtered =
        Object.keys(markerIconMap)
          ?.filter(id => markerIconMap[id].label.toLowerCase()?.includes(query?.toLowerCase()))
          .map(id => ({
            id,
            ...markerIconMap[id]
          })) || [];
      setResults(filtered);
    } else setResults(markers);
  };

  const handleSave = values => {
    const {
      type,
      label,
      grid,
      hasGlobalPrompt,
      globalPrompt,
      hasInternalIds,
      internalIds,
      hasHelp,
      help: elementHelp,
      fieldHelp,
      hasAddress,
      address,
      linkedGroup,
      hasARange,
      aMin,
      aMax,
      hasRange,
      min,
      max,
      rangeMin,
      rangeMax,
      allowDisable,
      enabledLabel,
      disabledLabel
    } = values;

    if (
      checksheetsUsingTarget?.length > 0 &&
      parentChanged &&
      !!checkhseetsUsingTargetNoSiblings?.length
    ) {
      setTempValues(watch());
      setShowModalConflict(true);
      return;
    }

    let shouldReturn = false;
    if (hasARange && !exists(aMin) && !exists(aMax)) {
      setAcceptableError("Either a min or max must be provided.");
      shouldReturn = true;
    } else setAcceptableError(null);
    if (hasRange && !exists(min) && !exists(max)) {
      setRestrictedError("Either a min or max must be provided.");
      shouldReturn = true;
    } else setRestrictedError(null);

    if (type === "range" && !(exists(rangeMin) && exists(rangeMax))) {
      setRangeError("Min and max must be provided.");
      shouldReturn = true;
    } else setRangeError(null);

    if (shouldReturn) return;

    let newHelp = elementHelp;
    if (targetElement.element !== FIELD) {
      if (elementHelp) newHelp = [...elementHelp.filter(({key, value}) => !!key && !!value)];
      if (fieldHelp)
        newHelp = [
          ...newHelp,
          ...fieldHelp
            .filter(({key, value}) => !!key && !!value)
            .map(item => {
              delete item.enabled;
              return item;
            })
        ];
    }

    const element = {
      ...targetElement,
      ...values,
      label,
      grid
    };

    if (targetElement.element === FIELD && type) element.type = type;

    let elementParent;
    if (linkedGroup && linkedGroup !== "") elementParent = linkedGroup;
    else elementParent = targetElement.parentName || "";

    if (hasGlobalPrompt) element.globalPrompt = globalPrompt;
    else delete element.globalPrompt;

    if (hasInternalIds) element.internalIds = internalIds;
    else delete element.internalIds;

    if (hasHelp || (element.element === FIELD && newHelp)) element.help = newHelp;
    else delete element.help;

    if (hasAddress) element.address = address;
    else delete element.address;

    if (checksheets?.length > 0 && type !== targetElement.type) {
      stored.current = {...element, parentName: elementParent};
      setShowModalConfirm(true);
    }

    if (allowDisable) {
      element.allowDisable = true;
      element.enabledLabel = enabledLabel;
      element.disabledLabel = disabledLabel;
    } else {
      if ("allowDisable" in element) element.allowDisable = false;
      delete element.enabledLabel;
      delete element.disabledLabel;
    }

    persistEdit({...element, parentName: elementParent});
  };

  const renderGroupDropdown = () => {
    const linkedGroups = builder.allIds
      .map(id => builder.byId[id])
      .filter(el => el?.element === GROUP);
    return (
      linkedGroups.length > 0 && (
        <FormField>
          <InputSelect
            testId="edit.linkedGroup"
            name="linkedGroup"
            options={linkedGroups}
            label="Parent Group"
            defaultValue={targetElement.parentName}
          />
        </FormField>
      )
    );
  };

  const renderPositionDropdown = () =>
    positionOptions.length > 0 && (
      <FormField>
        <InputSelect
          testId="edit.position"
          name="position"
          options={positionOptions}
          label="Position"
        />
      </FormField>
    );

  const renderUniqueWarning = () => {
    const warnings = [];
    const p = [];

    watchElement?.internalIds?.map(({value}, index) => {
      if (index > 0 && watchElement.internalIds[index - 1])
        p.push(watchElement.internalIds[index - 1].value);

      if (value !== "" && p?.includes(value))
        warnings.push(`${value} exists in current ${targetElement.element.toUpperCase()} help`);

      Object.keys(builder.byId).map(id => {
        const target = builder.byId[id];
        if (
          value !== "" &&
          target.name !== targetElement.name &&
          target?.internalIds?.filter(({value: val}) => val === value).length === 1
        )
          warnings.push(`${value} exists in ${target.label} ${target.element.toUpperCase()} help`);
      });
    });

    let message = "";
    if (warnings.length === 1) message = warnings.toString();
    if (warnings.length === 2) message = warnings.join(" and ");
    if (warnings.length > 2)
      warnings.map((warning, index) => {
        message = +index < warnings.length - 1 ? `${warning}, ` : `and ${warning}`;
      });

    return (
      warnings?.length > 0 && (
        <Duplicates>
          <span>Warning!</span> The following values are not unique. {message}.
        </Duplicates>
      )
    );
  };

  const getComponentLink = useCallback(
    checksheetId => `/facilities/${slug}/checksheets/${checksheetId}/edit#${targetElement.name}`,
    [slug, targetElement]
  );

  const renderOptions = ({
    element,
    hasGlobalPrompt,
    hasInternalIds,
    hasHelp,
    hasAddress,
    markerId
  }) => (
    <FormGroup>
      {element === COMPONENT && (
        <FormField>
          <InputCheck name="grid" testId="edit.grid">
            Display Component as grid in form&nbsp;
            <Help>Show fields within this component horizontally.</Help>
          </InputCheck>
        </FormField>
      )}
      {element === GROUP && (
        <FormField>
          <InputCheck name="hasGlobalPrompt" testId="edit.hasGlobalPrompt">
            Do you want to add a global prompt&nbsp;
            <Help>
              Provide a general description that will be shown at the top of {targetElement.label}
            </Help>
          </InputCheck>
        </FormField>
      )}
      {hasGlobalPrompt && (
        <FormField>
          <InputTextArea
            name="globalPrompt"
            testId="edit.globalPrompt"
            placeholder="Prompt for all components in group."
          />
        </FormField>
      )}
      {element === GROUP && (
        <FormField>
          <InputCheck name="hasInternalIds" testId="edit.hasInternalIds">
            Do you want to add Internal Ids&nbsp;
            <Help>
              Provide key value pairs to identify groups. These only show in the facility builder.
            </Help>
          </InputCheck>
          {hasInternalIds && (
            <InputTextGroup
              name="internalIds"
              label={`INTERNAL IDS${element === COMPONENT ? " / SERIAL #" : ""}`}
              canSetKey
            />
          )}
          {renderUniqueWarning()}
        </FormField>
      )}
      <FormField>
        <InputCheck name="hasHelp" testId="edit.hasHelp">
          Do you want to provide additional help&nbsp;
          <Help>
            These descriptions will be available when filling out {targetElement.label}&apos;s
            fields.
          </Help>
        </InputCheck>
      </FormField>
      {hasHelp && (
        <>
          <InputFileGroup name="files" callback={addFile} />
          <FormField>
            <ElementHelp
              testId="edit.help"
              name="help"
              label={`${element.toUpperCase()} HELP`}
              triggerAppend={triggerAppend}
            />
          </FormField>

          {hasChildFields && (
            <FormField>
              <ElementFieldHelp name="fieldHelp" label="FIELD HELP" builder={builder} />
            </FormField>
          )}
        </>
      )}
      {element === GROUP && (
        <>
          <FormField>
            <InputCheck name="hasAddress" testId="edit.hasAddress">
              Provide location&nbsp;
              <Help>These locations will show as markers on the site map.</Help>
            </InputCheck>
          </FormField>
          {hasAddress && (
            <>
              <Location />

              {markers?.length > 0 && element === GROUP && (
                <FormField>
                  <SearchWrapper>
                    <SearchSelect
                      label="Map Marker"
                      search={search}
                      results={results}
                      setResults={setResults}
                      add={({id}) => setValue("markerId", id)}
                      showAll
                    />
                  </SearchWrapper>
                </FormField>
              )}

              <button type="button" onClick={() => setValue("markerId", null)}>
                <Marker>
                  {markerIconMap[markerId]?.label || DEFAULT_MARKER.label}
                  <Icon
                    icon={markerIconMap[markerId]?.icon || DEFAULT_MARKER.icon}
                    color={`#${markerIconMap[markerId]?.color || DEFAULT_MARKER.color}`}
                  />
                  {markerIconMap[markerId]?.label && <Icon icon={faClose} />}
                </Marker>
              </button>
            </>
          )}
        </>
      )}
      {element === COMPONENT && (
        <>
          <InputCheck name="allowDisable">
            Allow component to be disabled&nbsp;
            <Help>
              Enables a Component Service State (enabled/disabled) for this component. Provide
              custom labels to describe each state, allowing field conditions to depend on it.
            </Help>
          </InputCheck>
          {watchAllowDisable && (
            <LabelsWrapper>
              <FormField>
                <InputText name="enabledLabel" required label="Enabled State Label" />
              </FormField>
              <FormField>
                <InputText name="disabledLabel" required label="Disabled State Label" />
              </FormField>
            </LabelsWrapper>
          )}
        </>
      )}
    </FormGroup>
  );

  const getLinkedGroup = (grandparent, linkedGroups) => {
    const groups = linkedGroups.map(item => item.name);
    if (groups.includes(targetElement.parentName)) return targetElement.parentName;
    if (grandparent && groups.includes(grandparent)) return grandparent;
    return null;
  };

  const renderFieldOptions = () => {
    const linkedGroups = builder.allIds
      .map(id => builder.byId[id])
      .filter(el => el?.element === GROUP && el?.toggle);
    const grandparent =
      targetElement.parentName in builder.byId
        ? builder.byId[targetElement.parentName].parentName
        : null;

    const currentGroup = getLinkedGroup(grandparent, linkedGroups);
    const linkedComponents =
      watchLinkedGroup && builder.byId[watchLinkedGroup]?.children
        ? builder.byId[watchLinkedGroup].children
            .map(id => builder.byId[id])
            .filter(el => el?.element === COMPONENT && el?.toggle)
        : [];
    return (
      <>
        {linkedGroups.length > 0 && (
          <FormField>
            <InputSelect
              testId="edit.linkedGroup"
              name="linkedGroup"
              options={linkedGroups}
              label="Linked Group"
              placeholder="Link to Group..."
              defaultValue={currentGroup}
            />
          </FormField>
        )}
        {linkedComponents.length > 0 && (
          <FormField>
            <InputSelect
              testId="edit.linkedComponent"
              name="linkedComponent"
              options={linkedComponents}
              placeholder="Link to Component..."
              label="Linked Component"
              defaultValue={
                linkedComponents.map(item => item.name).includes(targetElement.parentName)
                  ? targetElement.parentName
                  : null
              }
            />
          </FormField>
        )}
      </>
    );
  };

  if (showModalConfirm)
    return (
      <Modal visible={showModalConfirm} setVisible={setShowModalConfirm} allowClose={false}>
        <Warning>Warning!</Warning>
        <Text>
          Changing a field&apos;s type will detach previous responses and this field will be treated
          as a new field. Are you sure you want to proceed?
        </Text>
        <br />
        <Inline>
          <Button
            type="button"
            onClick={() => {
              persistEdit(stored.current);
            }}>
            Confirm
          </Button>
          <Button type="button" onClick={() => setShowModalConfirm(false)}>
            Cancel
          </Button>
        </Inline>
      </Modal>
    );

  if (saveWarningAction)
    return (
      <Modal visible setVisible={setVisible} hasBackButton goBack={() => saveWarningAction(false)}>
        <HeadingCenter>Discard Changes?</HeadingCenter>
        <Text>
          Are you sure you want to leave this page? You have unsaved changes that will be lost.
        </Text>
        <br />
        <Inline>
          <WarningButton type="button" onClick={() => saveWarningAction(true)}>
            Continue and Discard
          </WarningButton>
          <Button type="button" onClick={() => saveWarningAction(false)}>
            Cancel
          </Button>
        </Inline>
      </Modal>
    );

  if (showModalConflict)
    return (
      <ModalEditConflict
        visible={showModalConflict}
        setVisible={setVisible}
        goBack={() => setShowModalConflict(false)}
        checksheetsNoSiblings={checkhseetsUsingTargetNoSiblings}
        goToChecksheet={id => {
          const link = getComponentLink(id);
          if (intermediateAction) intermediateAction(link);
          else navigate(link);
        }}
      />
    );

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <HeadingCenter>Edit {toTitleCase(targetElement.element)}</HeadingCenter>

      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(handleSave)} noValidate>
          <FormGroup>
            {targetElement.element === COMPONENT && renderGroupDropdown(targetElement)}
            {(targetElement.element === GROUP || targetElement.element === COMPONENT) &&
              renderPositionDropdown()}
            {targetElement.element !== FIELD && (
              <FormField>
                <InputText
                  testId="edit.label"
                  name="label"
                  label="NAME"
                  placeholder={targetElement.label}
                  required
                />
              </FormField>
            )}
            {targetElement.element !== FIELD && renderOptions({...targetElement, ...watchElement})}
          </FormGroup>
          {targetElement.element === FIELD && (
            <>
              {renderFieldOptions()}
              <FieldBuilder
                field={{...targetElement, ...watchElement}}
                editElement
                restrictedError={restrictedError}
                acceptableError={acceptableError}
                rangeError={rangeError}
                units={units}
                disallowed={[
                  "text",
                  "textarea",
                  "checkbox",
                  "confirm",
                  "multiselect",
                  "switch",
                  "radio",
                  "date",
                  "time",
                  "dropdown",
                  "upload"
                ]}
              />
            </>
          )}
          {checksheetsUsingTarget?.length > 0 &&
            parentChanged &&
            !checkhseetsUsingTargetNoSiblings?.length && (
              <WarningWrapper>
                <WarningHeader>Warning!</WarningHeader>&nbsp;Moving this component will apply the
                same change to corresponding checksheets.
              </WarningWrapper>
            )}
          <Button data-testid="edit.save" type="submit">
            Save
          </Button>
        </Form>
      </FormProvider>
    </Modal>
  );
};

ModalEditElement.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  targetElement: PropTypes.objectOf(PropTypes.any).isRequired,
  setTargetElement: PropTypes.func.isRequired,
  persistEdit: PropTypes.func.isRequired,
  builder: PropTypes.objectOf(PropTypes.any).isRequired,
  markerIconMap: PropTypes.objectOf(PropTypes.any),
  checksheets: PropTypes.arrayOf(PropTypes.any),
  units: PropTypes.arrayOf(PropTypes.any),
  saveWarningAction: PropTypes.func
};

// Style Overrides
const Marker = styled(Pill)`
  background: ${({theme}) => theme.secondary};
`;

const Icon = styled(FontAwesomeIcon)`
  padding-left: ${pad / 2}px;
  fill: ${({color, theme}) => color ?? theme.tertiary};
`;

const Duplicates = styled(Text)`
  ${voice.quiet};

  span {
    color: ${({theme}) => theme.warning};
  }
`;

const WarningWrapper = styled(Warning)`
  margin-bottom: ${pad / 2}px;
  color: ${({theme}) => theme.secondary};
`;

const WarningHeader = styled.strong`
  color: ${({theme, error}) => (error ? theme.error : theme.warning)};
`;

const WarningButton = styled(Button)`
  color: ${({theme}) => theme.error};
`;

const LabelsWrapper = styled.div`
  width: 200px;
`;

export default ModalEditElement;
